import { Injectable, signal } from '@angular/core';
import {
  DEFAULT_THEME_ID,
  THEME_PALETTES,
  ThemePalette,
} from '../../constants/theme-palettes.constant';

const STORAGE_KEY = 'trieon-ui-theme';
const COLOR_MODE_KEY = 'trieon-ui-color-mode';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly palettes = THEME_PALETTES;
  readonly selectedPaletteId = signal(DEFAULT_THEME_ID);
  readonly isDark = signal(false);

  init(): void {
    const savedId = localStorage.getItem(STORAGE_KEY);
    const palette = this.findPalette(savedId) ?? this.findPalette(DEFAULT_THEME_ID)!;
    this.applyPalette(palette, false);
    this.applyColorMode(localStorage.getItem(COLOR_MODE_KEY) === 'dark', false);
  }

  selectPalette(id: string): void {
    const palette = this.findPalette(id);
    if (!palette) {
      return;
    }
    this.applyPalette(palette, true);
  }

  toggleDarkMode(): void {
    this.applyColorMode(!this.isDark(), true);
  }

  setDarkMode(dark: boolean): void {
    this.applyColorMode(dark, true);
  }

  getSelectedPalette(): ThemePalette {
    return this.findPalette(this.selectedPaletteId()) ?? THEME_PALETTES[0];
  }

  private findPalette(id: string | null | undefined): ThemePalette | undefined {
    if (!id) {
      return undefined;
    }
    return THEME_PALETTES.find((palette) => palette.id === id);
  }

  private applyColorMode(dark: boolean, persist: boolean): void {
    this.isDark.set(dark);
    document.documentElement.classList.toggle('dark', dark);
    if (persist) {
      localStorage.setItem(COLOR_MODE_KEY, dark ? 'dark' : 'light');
    }
  }

  private applyPalette(palette: ThemePalette, persist: boolean): void {
    this.selectedPaletteId.set(palette.id);

    const root = document.documentElement;
    const vars: Record<string, string> = {
      '--tp-primary': palette.primary,
      '--tp-primary-hover': palette.hover,
      '--tp-primary-light': palette.light,
      '--tp-primary-text': palette.primary,
      '--tp-btn-primary': palette.primary,
      '--tp-btn-primary-hover': palette.hover,
      '--tp-brand-teal': palette.primary,
      '--tp-brand-teal-light': palette.hover,
      '--tp-brand-cyan-pale': palette.light,
    };

    for (const [key, value] of Object.entries(vars)) {
      root.style.setProperty(key, value);
    }

    root.setAttribute('data-theme', palette.id);

    if (persist) {
      localStorage.setItem(STORAGE_KEY, palette.id);
    }
  }
}
